import {useEffect, useState} from "react";
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Pagination,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PeopleIcon from "@mui/icons-material/People";
import {Link as RouterLink} from "react-router-dom";
import {useTranslation} from "react-i18next";

import {useAlert} from "../context/AlertContext";
import {useAuth} from "../context/AuthContext";
import AppTableHeaderCell from "./AppTableHeaderCell";
import {getMyStreams} from "../api/api";
import {Stream} from "../types";

interface Props {
    refreshTrigger: boolean;
    refreshStreams: () => void;
}

const getInitials = (name?: string) => {
    if (!name || !name.trim()) return "?";

    const parts = name.trim().split(/\s+/);

    if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
    }

    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
};

const MyStreamsList = ({refreshTrigger}: Props) => {
    const {t} = useTranslation();
    const {showAlert} = useAlert();
    const {hasRole} = useAuth();
    const canViewUsers = hasRole("ROLE_TEACHER") || hasRole("ROLE_ADMIN");

    const [streams, setStreams] = useState<Stream[]>([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const pageSize = 10;

    const [searchName, setSearchName] = useState("");
    const [searchInput, setSearchInput] = useState("");

    const [loading, setLoading] = useState(false);

    const fetchStreamsPage = async () => {
        try {
            setLoading(true);

            const {streams, totalPages} = await getMyStreams(page - 1, pageSize, searchName);

            setStreams(streams);
            setTotalPages(totalPages);
        } catch (error: any) {
            showAlert(error?.message ?? t("mystreamlist.errors.loadFailed"), "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStreamsPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, refreshTrigger, searchName]);

    useEffect(() => {
        setPage(1);
    }, [searchName]);

    return (
        <>
            {loading ? (
                <Box sx={{display: "flex", justifyContent: "center", py: 8}}>
                    <CircularProgress/>
                </Box>
            ) : (
                <Paper
                    elevation={0}
                    sx={(theme) => ({
                        borderRadius: 3,
                        overflow: "hidden",
                        backgroundColor: theme.palette.background.paper,
                    })}
                >
                    <Box
                        sx={(theme) => ({
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            px: {xs: 2, md: 3},
                            py: 2,
                            flexWrap: "wrap",
                            backgroundColor: theme.palette.background.paper,
                            borderBottom: `1px solid ${theme.palette.action.hover}`,
                        })}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                width: {xs: "100%", sm: "auto"},
                            }}
                        >
                            <TextField
                                type="search"
                                size="small"
                                placeholder={t("mystreamlist.searchByStreamName")}
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        setSearchName(searchInput.trim());
                                    }
                                }}
                                sx={{
                                    width: {xs: "100%", sm: 320},
                                    flexGrow: {xs: 1, sm: 0},
                                }}
                            />

                            <IconButton
                                onClick={() => setSearchName(searchInput.trim())}
                                disabled={loading}
                                aria-label={t("common.search")}
                                sx={(theme) => ({
                                    width: 40,
                                    height: 40,
                                    borderRadius: "50%",
                                    backgroundColor: theme.palette.action.hover,
                                    color: theme.palette.text.primary,
                                    flexShrink: 0,
                                    "&:hover": {
                                        backgroundColor: theme.palette.action.selected,
                                    },
                                })}
                            >
                                <SearchIcon fontSize="small"/>
                            </IconButton>
                        </Box>

                        <Box sx={{flexGrow: 1}}/>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                width: {xs: "100%", sm: "auto"},
                                textAlign: {xs: "center", sm: "right"},
                            }}
                        >
                            {t("common.pageOf", {page, totalPages})}
                        </Typography>
                    </Box>

                    {streams.length === 0 ? (
                        <Box sx={{py: 10, px: 3, textAlign: "center"}}>
                            <Typography variant="h6" sx={{mb: 1}}>
                                {t("mystreamlist.noStreamsFound")}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                {t("mystreamlist.tryAnotherStreamName")}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Box sx={{display: {xs: "none", md: "block"}, width: "100%", overflowX: "auto"}}>
                                <table style={{width: "100%", borderCollapse: "separate", borderSpacing: 0}}>
                                    <thead>
                                    <tr style={{background: "#fafafa"}}>
                                        <AppTableHeaderCell>
                                            {t("mystreamlist.stream")}
                                        </AppTableHeaderCell>

                                        <AppTableHeaderCell>
                                            {t("mystreamlist.startingDate")}
                                        </AppTableHeaderCell>

                                        <AppTableHeaderCell>
                                            {t("mystreamlist.endingDate")}
                                        </AppTableHeaderCell>

                                        <AppTableHeaderCell align="right">
                                            {t("common.actions")}
                                        </AppTableHeaderCell>
                                    </tr>
                                    </thead>

                                    <tbody>
                                    {streams.map((stream) => (
                                        <tr
                                            key={stream.id}
                                            style={{
                                                transition: "background-color 0.2s ease",
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = "#fafafa";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = "transparent";
                                            }}
                                        >
                                            <td style={{padding: "18px 16px"}}>
                                                <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                                                    <Box
                                                        sx={{
                                                            width: 40,
                                                            height: 40,
                                                            borderRadius: "50%",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            fontSize: 13,
                                                            fontWeight: 700,
                                                            bgcolor: "grey.300",
                                                            color: "text.primary",
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {getInitials(stream.name)}
                                                    </Box>

                                                    <Typography variant="body2" sx={{fontWeight: 600}}>
                                                        {stream.name || "—"}
                                                    </Typography>
                                                </Box>
                                            </td>

                                            <td style={{padding: "18px 16px"}}>
                                                <Typography variant="body2">
                                                    {stream.startingDate || "—"}
                                                </Typography>
                                            </td>

                                            <td style={{padding: "18px 16px"}}>
                                                <Typography variant="body2">
                                                    {stream.endingDate || "—"}
                                                </Typography>
                                            </td>

                                            <td style={{padding: "18px 16px", textAlign: "right"}}>
                                                <Box sx={{display: "inline-flex", gap: 1}}>
                                                    <Tooltip title={t("mystreamlist.viewSchedule")}>
                                                        <IconButton
                                                            size="small"
                                                            component={RouterLink}
                                                            to={`/my-streams/${stream.id}`}
                                                            aria-label={t("mystreamlist.viewSchedule")}
                                                            sx={(theme) => ({
                                                                width: 36,
                                                                height: 36,
                                                                borderRadius: "50%",
                                                                backgroundColor: theme.palette.action.hover,
                                                                color: theme.palette.text.primary,
                                                                "&:hover": {
                                                                    backgroundColor: theme.palette.action.selected,
                                                                },
                                                            })}
                                                        >
                                                            <CalendarMonthIcon fontSize="small"/>
                                                        </IconButton>
                                                    </Tooltip>

                                                    {canViewUsers && (
                                                        <Tooltip title={t("mystreamlist.viewUsers")}>
                                                            <IconButton
                                                                size="small"
                                                                component={RouterLink}
                                                                to={`/my-streams/${stream.id}/users`}
                                                                aria-label={t("mystreamlist.viewUsers")}
                                                                sx={(theme) => ({
                                                                    width: 36,
                                                                    height: 36,
                                                                    borderRadius: "50%",
                                                                    backgroundColor: theme.palette.action.hover,
                                                                    color: theme.palette.text.primary,
                                                                    "&:hover": {
                                                                        backgroundColor: theme.palette.action.selected,
                                                                    },
                                                                })}
                                                            >
                                                                <PeopleIcon fontSize="small"/>
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </Box>
                            <Box
                                sx={{
                                    display: {xs: "grid", md: "none"},
                                    gap: 1.5,
                                    p: 1.5,
                                }}
                            >
                                {streams.map((stream) => (
                                    <Box
                                        key={stream.id}
                                        sx={(theme) => ({
                                            p: 2,
                                            borderRadius: 2.5,
                                            border: `1px solid ${theme.palette.action.hover}`,
                                            backgroundColor: theme.palette.background.paper,
                                            boxShadow:
                                                theme.palette.mode === "dark"
                                                    ? "0 10px 28px rgba(0,0,0,0.25)"
                                                    : "0 10px 28px rgba(15,23,42,0.06)",
                                        })}
                                    >
                                        <Box sx={{display: "flex", alignItems: "flex-start", gap: 1.5}}>
                                            <Box
                                                sx={{
                                                    width: 44,
                                                    height: 44,
                                                    borderRadius: "50%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontSize: 13,
                                                    fontWeight: 800,
                                                    bgcolor: "grey.300",
                                                    color: "text.primary",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {getInitials(stream.name)}
                                            </Box>

                                            <Box sx={{minWidth: 0, flexGrow: 1}}>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{display: "block", mb: 0.3}}
                                                >
                                                    {t("mystreamlist.stream")}
                                                </Typography>

                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        fontWeight: 700,
                                                        lineHeight: 1.35,
                                                        wordBreak: "break-word",
                                                    }}
                                                >
                                                    {stream.name || "—"}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Box
                                            sx={(theme) => ({
                                                mt: 2,
                                                display: "grid",
                                                gridTemplateColumns: "1fr",
                                                gap: 1,
                                                p: 1.2,
                                                borderRadius: 2,
                                                backgroundColor: theme.palette.action.hover,
                                            })}
                                        >
                                            <Box sx={{display: "flex", justifyContent: "space-between", gap: 2}}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {t("mystreamlist.startingDate")}
                                                </Typography>

                                                <Typography
                                                    variant="body2"
                                                    sx={{fontWeight: 600, textAlign: "right"}}
                                                >
                                                    {stream.startingDate || "—"}
                                                </Typography>
                                            </Box>

                                            <Box sx={{display: "flex", justifyContent: "space-between", gap: 2}}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {t("mystreamlist.endingDate")}
                                                </Typography>

                                                <Typography
                                                    variant="body2"
                                                    sx={{fontWeight: 600, textAlign: "right"}}
                                                >
                                                    {stream.endingDate || "—"}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Box
                                            sx={{
                                                mt: 1.5,
                                                display: "grid",
                                                gridTemplateColumns: canViewUsers ? "1fr 1fr" : "1fr",
                                                gap: 1,
                                            }}
                                        >
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                component={RouterLink}
                                                to={`/my-streams/${stream.id}`}
                                                startIcon={<CalendarMonthIcon fontSize="small"/>}
                                                sx={{
                                                    borderRadius: 2,
                                                    textTransform: "none",
                                                    fontWeight: 600,
                                                    py: 0.8,
                                                }}
                                            >
                                                {t("mystreamlist.schedule")}
                                            </Button>

                                            {canViewUsers && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    component={RouterLink}
                                                    to={`/my-streams/${stream.id}/users`}
                                                    startIcon={<PeopleIcon fontSize="small"/>}
                                                    sx={{
                                                        borderRadius: 2,
                                                        textTransform: "none",
                                                        fontWeight: 600,
                                                        py: 0.8,
                                                    }}
                                                >
                                                    {t("mystreamlist.users")}
                                                </Button>
                                            )}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </>
                    )}
                </Paper>
            )}

            <Box sx={{display: "flex", justifyContent: "center", mt: 3}}>
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                    color="primary"
                    disabled={loading}
                />
            </Box>
        </>
    );
};

export default MyStreamsList;